import axios from 'axios';
import qs from 'qs';
import { isPromise } from '@/scripts/validate';
import domain from '@/scripts/domain';
import axiosCanceler from './axiosCancel';
import ContentTypeEnum from './contentType';

const getConfig = (baseConfig = {}, contentType) => {
  if (!contentType) return baseConfig || {};
  let headers = {};
  if (baseConfig.headers) {
    headers = { ...baseConfig.headers };
  }
  if (contentType) {
    headers['Content-Type'] = contentType;
  }
  return {
    ...baseConfig,
    headers,
  };
};

// 不需要 token 的接口列表
const noTokenUrls = [
  '/v1/auth/login',
  '/v1/auth/register',
  '/v1/auth/sendPhoneCode',
  '/v1/auth/third_login',
  '/v1/teaching/getSchoolsForSchoolAdmin2'
]

const requestInterceptors = config => {
  axiosCanceler.remove(config);
  axiosCanceler.add(config);

  // 检查是否是登录等不需要 token 的接口
  const isNoTokenUrl = noTokenUrls.some(url => config.url && config.url.includes(url))

  // 登录接口不需要 token，所以跳过
  if (!isNoTokenUrl) {
    // 添加token到请求头
    const localStoragePrefix = window.localStoragePrefix || ''
    const token = localStorage.getItem('examination_session_token')
                  || localStorage.getItem(`${localStoragePrefix}session_token`)
                  || localStorage.getItem('YUNQUE_EXAMINATION_session_token')
                  || localStorage.getItem(`${localStoragePrefix}session_id`)

    if (token) {
      config.headers = config.headers || {}
      config.headers.F_token = token
    }

    // 添加user_id到请求头
    const userId = localStorage.getItem('examination_user_id')
                   || localStorage.getItem(`${localStoragePrefix}user_id`)
                   || localStorage.getItem('YUNQUE_EXAMINATION_user_id')

    if (userId) {
      config.headers = config.headers || {}
      config.headers.user_id = userId
    }
  }

  if (config.headers && config.headers['Content-Type'] === ContentTypeEnum.FORM_URLENCODED) {
    if (config.data) {
      config.data = qs.stringify(config.data);
    }
  }
  return config;
};

export class Http {
  //  instance;

  constructor(baseURL, baseContentType, { onRequest, onResponse }) {
    this.instance = axios.create({
      baseURL,
      headers: {
        'Content-Type': baseContentType,
      },
    });
    this.instance.interceptors.request.use(
      config => {
        if (onRequest) {
          const returnVal = onRequest(config);
          if (isPromise(returnVal)) {
            return returnVal.then(conf => requestInterceptors(conf));
          }
          return requestInterceptors(returnVal);
        }
        return requestInterceptors(config);
      },
      error => {
        axiosCanceler.clear();

        return Promise.reject(error);
      }
    );
    this.instance.interceptors.response.use(
      response => {
        axiosCanceler.remove(response.config);
        return onResponse ? onResponse(response) : response;
      },
      error => {
        axiosCanceler.remove(error.config || {});
        if (axios.isCancel(error)) {
          // 是取消请求的错误
          console.warn(error);
          return Promise.reject({ ...error, isCancel: true });
        }
        // 处理 401 状态码，跳转到登录页面
        if (error.response && error.response.status === 401) {
          window.location.href = 'https://www.hhyunyue.com/exam/Login';
        }
        return onResponse ? onResponse(error) : Promise.reject(error);
      }
    );
  }

  get(url, query, config) {
    return this.instance.request({
      ...config,
      url,
      params: query,
      method: 'get',
    });
  }

  post(url, data, contentType, config) {
    return this.instance.request({
      ...getConfig(config, contentType),
      url,
      data,
      method: 'post',
    });
  }

  patch(url, data, contentType, config) {
    return this.instance.request({
      ...getConfig(config, contentType),
      url,
      data,
      method: 'patch',
    });
  }

  put(url, data, contentType, config) {
    return this.instance.request({
      ...getConfig(config, contentType),
      url,
      data,
      method: 'put',
    });
  }

  delete(url, query, contentType, config) {
    return this.instance.request({
      ...getConfig(config, contentType),
      url,
      params: query,
      method: 'delete',
    });
  }
}
